/**
 * API client.
 *
 * Every read goes through `aware-insights-api` — the browser never queries Atlas or the
 * search index directly (BUSINESS_RULES § Data sources, rule 2). Once Kinde lands, each call
 * carries the access token as a Bearer credential and the API validates it against Kinde's
 * JWKS per request.
 *
 * The API does not exist yet. Until it does, `USE_FIXTURES` routes these calls to an in-repo
 * fixture set that mirrors the prototype's seed data, so the surfaces are real and reviewable
 * now. The contract below is the thing that matters — swapping to the live API is a change to
 * this file only, and no component knows the difference.
 */
import type { AwareExplain, Meeting, MeetingDetail, Town } from '@/lib/types';
import type { Signal, SignalMatch } from '@/lib/signals';
import type { CatalogUnit } from '@/lib/countries';
import type { CountryCode } from '@/lib/tier';
import type { CorpusStats } from '@/lib/corpus';
import type { LegislativeRecord } from '@/lib/records';
import type { Topic, TopicMeeting, TopicTrends } from '@/lib/topics';
import type { AccountState, TopUpPack } from '@/lib/billing';
import type { CurrencyCode } from '@/lib/pricing';
import * as fixtures from '@/lib/fixtures';

/**
 * LIVE. Every path below has a matching route on `aware-insights-api`, which is linked as the
 * Static Web App's backend — so `/api/*` is same-origin and there is no CORS in the picture.
 *
 * The fixtures stay in the repo rather than being deleted: they are the only way to render
 * these surfaces without an account, and they document the exact response shape each route is
 * expected to return.
 */
const USE_FIXTURES = false;

/**
 * How the API client gets the current access token.
 *
 * Injected from `main.tsx` rather than imported, so this module never depends on the auth SDK.
 * That keeps the dependency one-directional: the data layer is testable without an auth
 * provider, and replacing the identity vendor touches one file instead of every fetch.
 *
 * Returns `null` before sign-in completes, which is correct — the call then goes out without a
 * credential and the API answers 401. That is the API's decision to make, not ours; a client
 * that guesses at entitlement is how gated content leaks.
 */
type TokenGetter = () => Promise<string | null | undefined>;

let getToken: TokenGetter = async () => null;

export function setTokenGetter(fn: TokenGetter): void {
  getToken = fn;
}

async function authHeaders(): Promise<HeadersInit> {
  const token = await getToken();
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`/api${path}`, { headers: await authHeaders() });
  if (!res.ok) throw new ApiError(`GET ${path} failed`, res.status);
  return (await res.json()) as T;
}

/**
 * Start a subscription checkout.
 *
 * Sends the PLAN, not a price, and never a user id. The API resolves the price from its own
 * catalog and stamps the account from the verified token — a request from this browser cannot
 * choose what it is charged or whose account gets the subscription. That is the reason there
 * is no payment SDK in this bundle.
 *
 * Returns a URL to send the customer to. Nothing is granted on return: entitlement arrives via
 * the provider's webhook, so the app must re-read the account rather than assume success from
 * having been redirected back.
 */
export interface CheckoutStart {
  transactionId: string;
  checkoutUrl: string | null;
}

export async function startCheckout(
  tier: string,
  currency: CurrencyCode,
  billingPeriod: 'monthly' | 'annual',
): Promise<CheckoutStart> {
  const res = await fetch('/api/checkout', {
    method: 'POST',
    headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ tier, currency, billingPeriod }),
  });
  if (!res.ok) throw new ApiError('Could not start checkout', res.status);
  return (await res.json()) as CheckoutStart;
}

/** Towns the signed-in user follows, in their own order. */
export async function fetchFollowedTowns(): Promise<Town[]> {
  if (USE_FIXTURES) return fixtures.followedTowns();
  return get<Town[]>('/towns');
}

/**
 * Recaps for one town, or the aggregated "Latest" view across every followed town.
 *
 * Filtering by body type and category happens server-side: on a phone this list is the whole
 * screen, and shipping the full set down to filter in the browser wastes the one thing the
 * performance budget cannot buy back.
 */
export interface MeetingQuery {
  townId: string;
  body?: string;
  category?: string;
  /** Narrow to a single meeting. `'all'` or omitted spans every meeting in scope. */
  meeting?: string;
  limit?: number;
  offset?: number;
}

/** One meeting a reader can narrow to, with how many summaries it holds. */
export interface MeetingOption {
  id: string;
  title: string;
  /** mm/dd/yyyy — the date the meeting was HELD, not when the record was ingested. */
  date: string;
  town: string;
  region: string;
  count: number;
  /** An agenda for a meeting that has not happened yet. */
  isUpcoming: boolean;
}

export interface MeetingPage {
  meetings: Meeting[];
  /** Total matching the filter, so the "N more" affordance can be honest. */
  total: number;
  /** Counts per body type for the filter tabs, computed over the unfiltered scope. */
  bodyCounts: Record<string, number>;
  /** Counts per category, in descending order of frequency. */
  categoryCounts: Record<string, number>;
  /** The meetings in scope, newest first, for the meeting filter. */
  meetingOptions: MeetingOption[];
}

export async function fetchMeetings(query: MeetingQuery): Promise<MeetingPage> {
  if (USE_FIXTURES) return fixtures.meetingPage(query);
  const params = new URLSearchParams();
  params.set('town', query.townId);
  if (query.body && query.body !== 'all') params.set('body', query.body);
  if (query.category && query.category !== 'all') params.set('category', query.category);
  if (query.meeting && query.meeting !== 'all') params.set('meeting', query.meeting);
  params.set('limit', String(query.limit ?? 10));
  params.set('offset', String(query.offset ?? 0));
  return get<MeetingPage>(`/meetings?${params.toString()}`);
}

/**
 * Aware Explain.
 *
 * A POST rather than a GET because it can consume a metered use. The server decides whether
 * this reader has already paid for this item — the generated text is cached globally, but the
 * per-user first-view ledger is what the meter reads from, so a cache hit is not automatically
 * free. The client must never assume either way.
 */
export async function explainMeeting(meetingId: string): Promise<AwareExplain> {
  if (USE_FIXTURES) return fixtures.explain(meetingId);
  const res = await fetch(`/api/meetings/${encodeURIComponent(meetingId)}/explain`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!res.ok) throw new ApiError('Aware Explain failed', res.status);
  return (await res.json()) as AwareExplain;
}

/** The user's Signals, with all-time match counts for the chip badges. */
export async function fetchSignals(): Promise<Signal[]> {
  if (USE_FIXTURES) return fixtures.signals();
  return get<Signal[]>('/signals');
}

/**
 * Signal Feed rows.
 *
 * Served from materialized match rows written by the precompute job, not from a live search
 * fan-out across every town — that is what keeps this surface a single indexed read rather
 * than a query against thousands of jurisdictions.
 *
 * Cross-town access is permitted for Intelligence and Intelligence Pro only. The tier check
 * lives in the API, ahead of the index call; it is never a parameter the client supplies.
 */
export async function fetchSignalMatches(signalName?: string): Promise<SignalMatch[]> {
  if (USE_FIXTURES) return fixtures.signalMatches(signalName);
  const params = new URLSearchParams();
  if (signalName) params.set('signal', signalName);
  return get<SignalMatch[]>(`/signal-feed?${params.toString()}`);
}

/**
 * The town catalogue for one country, grouped by its county-equivalent unit.
 *
 * Country access is plan-gated, and like every other entitlement the check belongs in the API
 * — a request for a country this plan does not include is rejected there, not hidden here.
 */
export async function fetchTownCatalog(country: CountryCode): Promise<CatalogUnit[]> {
  if (USE_FIXTURES) return fixtures.townCatalog(country);
  return get<CatalogUnit[]>(`/catalog?country=${encodeURIComponent(country)}`);
}

/** Follow or unfollow a town. The tier cap is enforced server-side, at follow time. */
export async function setTownFollowed(townName: string, followed: boolean): Promise<void> {
  if (USE_FIXTURES) return fixtures.setFollowed(townName, followed);
  const res = await fetch(`/api/towns/${encodeURIComponent(townName)}`, {
    method: followed ? 'PUT' : 'DELETE',
    headers: await authHeaders(),
  });
  if (!res.ok) throw new ApiError('Could not update followed towns', res.status);
}

/**
 * One meeting, with its structured extractions.
 *
 * Returned in full at every tier — reading a meeting is not tier-gated, only the structured
 * rail's presentation is. Each extraction carries its own `startTime` so a citation can seek
 * the source recording to the exact second.
 */
export async function fetchMeetingDetail(meetingId: string): Promise<MeetingDetail> {
  if (USE_FIXTURES) return fixtures.meetingDetail(meetingId);
  return get<MeetingDetail>(`/meetings/${encodeURIComponent(meetingId)}`);
}

/** Live corpus totals. Rounded DOWN at the point of display, never hardcoded. */
export async function fetchCorpusStats(): Promise<CorpusStats> {
  if (USE_FIXTURES) return fixtures.corpusStats();
  return get<CorpusStats>('/corpus');
}

export type SearchScope = 'all' | 'mine' | 'pick';

export interface SearchQuery {
  q: string;
  scope: SearchScope;
  /** Region keys like "US/NJ", only meaningful when scope is "pick". */
  regions?: string[];
  body?: string;
  category?: string;
}

export interface SearchResults {
  total: number;
  /** How many distinct cities the results span — the cross-city value made concrete. */
  cities: number;
  results: SignalMatch[];
  facets: {
    body: Record<string, number>;
    region: Record<string, number>;
    category: Record<string, number>;
  };
}

/**
 * Hybrid semantic + keyword search.
 *
 * "battery storage pause" has to match "moratorium on energy storage" — the copy says so
 * explicitly, so lexical-only matching would make the product's own promise false.
 *
 * Scope is a request, not a grant: the API applies the tier's own limits on top of whatever
 * is asked for. Cross-town scope is available to Intelligence and Intelligence Pro only, and
 * that check happens server-side, ahead of the index call.
 */
export async function runSearch(query: SearchQuery): Promise<SearchResults> {
  if (USE_FIXTURES) return fixtures.search(query.q);
  const params = new URLSearchParams({ q: query.q, scope: query.scope });
  for (const region of query.regions ?? []) params.append('region', region);
  if (query.body && query.body !== 'all') params.set('body', query.body);
  if (query.category && query.category !== 'all') params.set('category', query.category);
  return get<SearchResults>(`/search?${params.toString()}`);
}

export type SignalDraft = Omit<Signal, 'id' | 'total' | 'meta'> & { id?: string };

/**
 * Create or update a Signal.
 *
 * One Signal drives three outputs — it fills Signal Feed, it rolls matched items sharing an
 * ordinance number into Legislative History, and it optionally sends alerts. Delivery is a
 * setting on the Signal, not a separate object, which is why channels and frequency are
 * fields here rather than a separate alerts API.
 */
export async function saveSignal(draft: SignalDraft): Promise<Signal> {
  if (USE_FIXTURES) return fixtures.saveSignal(draft);
  const res = await fetch(draft.id ? `/api/signals/${draft.id}` : '/api/signals', {
    method: draft.id ? 'PUT' : 'POST',
    headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(draft),
  });
  if (!res.ok) throw new ApiError('Could not save signal', res.status);
  return (await res.json()) as Signal;
}

export async function deleteSignal(id: string): Promise<void> {
  if (USE_FIXTURES) return fixtures.deleteSignal(id);
  const res = await fetch(`/api/signals/${id}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  if (!res.ok) throw new ApiError('Could not delete signal', res.status);
}

/**
 * Legislative History records for this user.
 *
 * Scoped to the user by construction: a record exists because one of THEIR Signals matched, or
 * because they tracked a number by hand. This endpoint never enumerates the corpus, which is
 * what keeps the surface tractable across thousands of towns.
 */
export async function fetchRecords(): Promise<LegislativeRecord[]> {
  if (USE_FIXTURES) return fixtures.legislativeRecords();
  return get<LegislativeRecord[]>('/records');
}

export async function fetchRecord(id: string): Promise<LegislativeRecord> {
  if (USE_FIXTURES) return fixtures.legislativeRecord(id);
  return get<LegislativeRecord>(`/records/${encodeURIComponent(id)}`);
}

/** Tracked Topics. A separate collection from Signals — never merge the two. */
export async function fetchTopics(): Promise<Topic[]> {
  if (USE_FIXTURES) return fixtures.topics();
  return get<Topic[]>('/topics');
}

/**
 * Twelve months of trend data for one topic.
 *
 * Served from `mv_topic_trends`, a materialized view that already aggregates prevalence per
 * city per month — so this is an indexed read, not a live cross-city aggregation. It is also
 * why a brand-new topic renders twelve months immediately with no loading state: the corpus is
 * already processed.
 */
export async function fetchTopicTrends(topicId: string): Promise<TopicTrends> {
  if (USE_FIXTURES) return fixtures.topicTrends(topicId);
  return get<TopicTrends>(`/topics/${encodeURIComponent(topicId)}/trends`);
}

/**
 * The meetings behind a stat card or a leaderboard row.
 *
 * Always scoped to the topic AND the thing clicked. A drill-down that returns the same rows
 * whatever you clicked is the specific bug this endpoint exists to avoid.
 */
export async function fetchTopicMeetings(
  topicId: string,
  filter: { stat?: string; city?: string } = {},
): Promise<TopicMeeting[]> {
  if (USE_FIXTURES) return fixtures.topicMeetings(topicId, filter);
  const params = new URLSearchParams();
  if (filter.stat) params.set('stat', filter.stat);
  if (filter.city) params.set('city', filter.city);
  return get<TopicMeeting[]>(`/topics/${encodeURIComponent(topicId)}/meetings?${params}`);
}

/**
 * The signed-in account: plan, trial state, meters and channels.
 *
 * Entitlement here comes from the billing provider's recorded webhook state, never inferred
 * from the auth session — Kinde is the identity source of truth, the billing provider is the
 * subscription source of truth, and the join key is the Kinde user id.
 */
export async function fetchAccount(): Promise<AccountState> {
  if (USE_FIXTURES) return fixtures.account();
  return get<AccountState>('/account');
}

export async function fetchTopUpPacks(currency: CurrencyCode): Promise<TopUpPack[]> {
  if (USE_FIXTURES) return fixtures.topUpPacks(currency);
  return get<TopUpPack[]>(`/top-up-packs?currency=${currency}`);
}
